// Per-entry group labels for a queued-edits proposal Bundle: the proposer
// (AI/backend) tags entries that form one logical unit — e.g. a care plan
// together with its goals, interventions, and signature tasks — with a shared
// label at build time. The review UI clusters entries sharing a label under one
// group header with group-level approve/reject; decisions are still recorded
// per entry, so the apply/audit machinery is unchanged. The label doubles as
// the group key (grouping is by exact string match).
//
// ##EntryHandle Groups live on `Bundle.entry[*].extension` as a single
// `valueString` extension per entry, keyed by the entry it sits on.

import type { Bundle, BundleEntry } from '@medplum/fhirtypes'
import { QUEUED_EDITS_ENTRY_GROUP_EXTENSION } from '#src/codeSystems.ts'
import { entryHandle, matchesHandle } from '#src/queued-edits/entry-handle.ts'

// Record one group label for a single entry. Idempotent per entry — re-setting
// replaces the entry's prior group extension. Pass undefined to clear it.
// Mutates and returns the bundle.
export function setEntryGroup(
  bundle: Bundle,
  entry: string,
  group: string | undefined
): Bundle {
  const target = (bundle.entry ?? []).find((e) => matchesHandle(e, entry))
  if (!target) {
    return bundle
  }
  const others = (target.extension ?? []).filter(
    (e) => e.url !== QUEUED_EDITS_ENTRY_GROUP_EXTENSION
  )
  if (group) {
    target.extension = [
      ...others,
      { url: QUEUED_EDITS_ENTRY_GROUP_EXTENSION, valueString: group },
    ]
  } else if (others.length > 0) {
    target.extension = others
  } else {
    delete target.extension
  }
  return bundle
}

// Record several group labels at once, keyed by entry handle. Entries with no
// matching handle are ignored. Mutates and returns the bundle.
export function setEntryGroups(
  bundle: Bundle,
  groups: Record<string, string>
): Bundle {
  for (const [handle, group] of Object.entries(groups)) {
    setEntryGroup(bundle, handle, group)
  }
  return bundle
}

// The group label recorded for an entry, if any.
export function getEntryGroup(entry: BundleEntry): string | undefined {
  return entry.extension?.find(
    (e) => e.url === QUEUED_EDITS_ENTRY_GROUP_EXTENSION
  )?.valueString
}

interface EntryGroups {
  [handle: string]: string
}

// All recorded group labels keyed by entry handle.
export function getEntryGroups(bundle: Bundle): EntryGroups {
  const groups: EntryGroups = {}
  for (const entry of bundle.entry ?? []) {
    const group = getEntryGroup(entry)
    const handle = entryHandle(entry)
    if (group && handle) {
      groups[handle] = group
    }
  }
  return groups
}
